import { useRef, useState } from 'react';

import { Button } from '@gearbelt/ui/components';

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import ArrowDownIcon from '../icons/arrow-down';
import { CheckIcon } from '../icons/check';
import { CloseSmallIcon } from '../icons/close-small';
import { Badge } from './badge';

type ComboboxOption = { value: string; label: string; disabled?: boolean };

interface ComboboxBaseProps {
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  error?: boolean;
  disabled?: boolean;
  renderItem?: (option: ComboboxOption, isSelected: boolean) => React.ReactNode;
  'aria-label'?: string;
}

interface SingleComboboxProps extends ComboboxBaseProps {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
  renderSelectedValue?: (option: ComboboxOption) => React.ReactNode;
}

interface MultiComboboxProps extends ComboboxBaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

type ComboboxProps = SingleComboboxProps | MultiComboboxProps;

export function Combobox(props: ComboboxProps) {
  const {
    options,
    placeholder = 'Select option...',
    searchPlaceholder = 'Search...',
    emptyMessage = 'No option found.',
    className,
    error,
    renderItem,
    disabled,
    'aria-label': ariaLabel,
  } = props;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const commandListRef = useRef<HTMLDivElement>(null);

  const resetScroll = () => {
    if (commandListRef.current) {
      commandListRef.current.scrollTop = 0;
    }
  };

  const isSelected = (optionValue: string) => (props.multiple ? props.value.includes(optionValue) : props.value === optionValue);

  const selectedOptions = props.multiple ? options.filter((option) => props.value.includes(option.value)) : [];
  const hasSelection = props.multiple ? props.value.length > 0 : Boolean(props.value);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            'border-input focus:ring-ring flex min-h-[41px] w-full items-center justify-between rounded-md border-2 bg-white px-3 py-2 text-sm font-normal ring-offset-white hover:bg-inherit focus:outline-none disabled:cursor-not-allowed [&>span]:line-clamp-1',
            !hasSelection && 'text-grey-5 hover:text-grey-5',
            error && 'border-default-red',
            disabled && 'bg-grey-1 text-default-text cursor-not-allowed disabled:opacity-100',
            className,
          )}>
          {props.multiple ? (
            selectedOptions.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1">
                {selectedOptions.map((option) => (
                  <Badge key={option.value} variant="default" size="sm" className="font-normal">
                    {option.label}
                    <button
                      type="button"
                      aria-label={`Remove ${option.label}`}
                      className="ml-0.5 flex items-center"
                      onClick={(event) => {
                        event.stopPropagation();
                        props.onChange(props.value.filter((selected) => selected !== option.value));
                      }}>
                      <CloseSmallIcon size={16} />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-grey-5">{!disabled && placeholder}</span>
            )
          ) : props.value ? (
            props.renderSelectedValue ? (
              props.renderSelectedValue(options.find((option) => option.value === props.value) || { value: '', label: '' })
            ) : (
              <span>{options.find((option) => option.value === props.value)?.label}</span>
            )
          ) : (
            <span className="text-grey-5">{!disabled && placeholder}</span>
          )}
          {!disabled && <ArrowDownIcon className={cn('text-default-text h-4 w-4', disabled && 'opacity-50')} />}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'text-default-text data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 overflow-hidden rounded-md border bg-white shadow-md',
          'data-[side=bottom]:translate-y-0.5 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          'w-[var(--radix-popper-anchor-width)]',
          'p-0',
        )}>
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            onValueChange={(value) => {
              setSearch(value);
              resetScroll();
            }}
          />
          <CommandList ref={commandListRef}>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options
                .filter((option) => option.label.toLowerCase().includes(search.toLowerCase()))
                .map((option) => {
                  const selected = isSelected(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      onSelect={(currentValue) => {
                        if (option.disabled) return;
                        if (props.multiple) {
                          props.onChange(
                            props.value.includes(currentValue) ? props.value.filter((selected) => selected !== currentValue) : [...props.value, currentValue],
                          );
                        } else {
                          props.onChange(currentValue === props.value ? '' : currentValue);
                          setOpen(false);
                        }
                      }}
                      keywords={[option.label]}
                      className={cn(
                        'focus:bg-default-blue/10 focus:text-default-text relative flex w-full cursor-default items-center justify-between rounded-sm py-1.5 pr-2 pl-2 text-sm outline-none select-none',
                        option.disabled && 'cursor-not-allowed opacity-50',
                      )}>
                      {renderItem ? renderItem(option, selected) : option.label}
                      {props.multiple && selected && <CheckIcon size={16} className="text-default-blue ml-2 shrink-0" />}
                    </CommandItem>
                  );
                })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
